"use client";

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type Theme = "light" | "dark" | "system";

export interface ThemeProviderProps {
  children: ReactNode;
  /** What to render before localStorage has been read (SSR / hydration). */
  defaultTheme?: Theme;
  /** localStorage key used to persist the user's choice. */
  storageKey?: string;
  /**
   * Class names applied to `document.documentElement`. Defaults match the
   * Tailwind v4 convention (`@variant dark { ... }` keys off `.dark`).
   */
  classes?: { light: string; dark: string };
}

interface ThemeProviderState {
  /** What the user picked: "light" | "dark" | "system". */
  theme: Theme;
  /** What is actually rendering: "light" | "dark" (system → resolved). */
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeProviderState | null>(null);

const DEFAULT_CLASSES = { light: "light", dark: "dark" } as const;

function readSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(theme: Theme, systemTheme: "light" | "dark"): "light" | "dark" {
  return theme === "system" ? systemTheme : theme;
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "cakewalk-ui-theme",
  classes = DEFAULT_CLASSES,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return defaultTheme;
    const stored = window.localStorage.getItem(storageKey);
    return (stored as Theme | null) ?? defaultTheme;
  });

  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(() => readSystemTheme());

  // Subscribe to OS-level color-scheme changes so `theme === "system"` users
  // flip when their OS does.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Reflect the resolved theme onto `<html>` so Tailwind's `dark:` variant
  // (and any of our own CSS hooks) pick it up.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const resolved = resolveTheme(theme, systemTheme);
    root.classList.remove(classes.light, classes.dark);
    root.classList.add(resolved === "dark" ? classes.dark : classes.light);
    root.style.colorScheme = resolved;
  }, [theme, systemTheme, classes]);

  const setTheme = useCallback(
    (next: Theme) => {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(storageKey, next);
      }
      setThemeState(next);
    },
    [storageKey],
  );

  const value = useMemo<ThemeProviderState>(
    () => ({
      theme,
      resolvedTheme: resolveTheme(theme, systemTheme),
      setTheme,
    }),
    [theme, systemTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeProviderState {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside a <ThemeProvider>");
  }
  return ctx;
}
